import { useQuery } from '@tanstack/react-query';
import { useApi, useAuth } from '../../hooks';
import type { CareBlockDetail } from '../../models/careBlockDetail';

/** Query key for one block's detail — exported so the submit mutation can
 *  invalidate exactly the block it wrote to, not every block. */
export const careBlockDetailKey = (blockId: string, token?: string) => [
  'care-block-detail',
  blockId,
  token,
];

/**
 * Fetches one care block's detail + activity feed from the mock layer
 * (GET /private/care-block/:blockId). Mirrors `useCareOsPatient`'s auth-gated
 * react-query shape. Disabled while `blockId` is null, so opening the profile
 * fires requests only for the drawer that's open and for kanban cards whose
 * pill shows the block's latest symptoms feedback.
 */
export const useCareBlockDetail = (blockId: string | null) => {
  const { api } = useApi();
  const { isAuthenticated, loading, userToken } = useAuth();

  const { data, isLoading, isError } = useQuery<CareBlockDetail, Error>({
    queryKey: careBlockDetailKey(blockId ?? '', userToken),
    queryFn: () =>
      api({ route: `/private/care-block/${blockId}`, token: userToken }),
    enabled: isAuthenticated && !loading && !!userToken && !!blockId,
    refetchOnWindowFocus: false,
  });

  return { block: data, isLoading, isError };
};
